import { Box, Button, Checkbox, FormControl, HStack, Heading, Skeleton, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { HTTP } from '@utils/http';
import FloatingLabelInput from 'components/FloatingLabelInput';
import { useFetchMe } from 'hooks/useApiQueries';
import { t } from 'i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { MindbodyUser } from 'types/client';

type FormData = Pick<
  MindbodyUser,
  | 'FirstName'
  | 'LastName'
  | 'AddressLine1'
  | 'PostalCode'
  | 'City'
  | 'MobilePhone'
  | 'Email'
  | 'SendPromotionalEmails'
  | 'SendScheduleEmails'
>;

export function ProfileEdit() {
  const { isLoading } = useFetchMe();

  return (
    <Skeleton isLoaded={!isLoading}>
      <ProfileEditForm />
    </Skeleton>
  );
}

function ProfileEditForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useFetchMe();
  function getDefaultValues(): Partial<FormData> {
    if (!user) return {};
    const {
      FirstName,
      LastName,
      AddressLine1,
      PostalCode,
      City,
      MobilePhone,
      Email,
      SendPromotionalEmails,
      SendScheduleEmails,
    } = user;
    return {
      FirstName,
      LastName,
      AddressLine1,
      PostalCode,
      City,
      MobilePhone,
      Email,
      SendPromotionalEmails,
      SendScheduleEmails,
    };
  }

  const formMethods = useForm<FormData>({
    defaultValues: getDefaultValues(),
  });

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  const onSubmit = async (formData: Partial<MindbodyUser>) => {
    try {
      await HTTP.post<{ Client: MindbodyUser }>('/client/updateclient', formData).send();
      // Le serveur vient de staler le miroir profil : forcer le rechargement de /client/me
      await queryClient.invalidateQueries({ queryKey: ['/client/me'] });
      navigate('/account');
    } catch (error) {
      // Error handled
    }
  };

  return (
    <Box as={FormProvider} {...formMethods} w="full">
      <HStack width="100%" justifyContent="space-between" margin="1em 0 2em">
        <Heading className="accountMainTitle" size="md" textTransform="uppercase">
          Modifier mes informations
        </Heading>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Retour
        </Button>
      </HStack>

      <VStack as="form" onSubmit={handleSubmit(onSubmit)} spacing={4} align="stretch" padding="1em 0">
        <FormControl isRequired>
          <FloatingLabelInput label={t('Nom')} {...register('LastName')} />
        </FormControl>

        <FormControl isRequired>
          <FloatingLabelInput label={t('Prénom')} {...register('FirstName')} />
        </FormControl>

        <FormControl>
          <FloatingLabelInput label={t('Adresse')} {...register('AddressLine1')} />
        </FormControl>

        <HStack>
          <FormControl isRequired>
            <FloatingLabelInput label={t('Code Postal')} {...register('PostalCode')} />
          </FormControl>
          <FormControl isRequired>
            <FloatingLabelInput label={t('Ville')} {...register('City')} />
          </FormControl>
        </HStack>

        <FormControl isRequired>
          <FloatingLabelInput label={t('Téléphone')} {...register('MobilePhone')} />
        </FormControl>

        <FormControl isRequired>
          <FloatingLabelInput disabled={true} label={t('Email')} {...register('Email')} />
        </FormControl>

        <FormControl textAlign={'left'}>
          <Checkbox borderColor="black" {...register('SendScheduleEmails')} textAlign={'left'}>
            Recevoir les informations importantes concernant mes réservations et le planning (recommandé)
          </Checkbox>
        </FormControl>

        <FormControl textAlign={'left'}>
          <Checkbox borderColor="black" {...register('SendPromotionalEmails')} textAlign={'left'}>
            Recevoir les actualités, événements et offres
          </Checkbox>
        </FormControl>

        <VStack padding="2em 1em 0">
          <Button variant="contained" type="submit" alignContent={'center'} mt={4} isLoading={isSubmitting}>
            METTRE À JOUR
          </Button>
        </VStack>
      </VStack>
    </Box>
  );
}
